import {Component, computed, HostListener, OnInit, signal, TemplateRef, ViewChild, ViewContainerRef} from '@angular/core';
import {PortalModule, TemplatePortal} from '@angular/cdk/portal';
import {CodaltComponent} from '../codalt.component';
import {AzureService, AzureWeeklyPullRequest} from '../services/azure.service';
import {Overlay, OverlayRef} from '@angular/cdk/overlay';
import {RegistrationService} from '../services/registration.service';
import {UserService} from '../services/user/user.service';
import {NgSelectComponent} from '@ng-select/ng-select';
import {FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {ControlsOf} from '@ngneat/reactive-forms';
import {LocalStorage} from '../storage.class';
import {ActivatedRoute} from '@angular/router';
import {User} from '../classes/user.class';
import {DayUser, ReportComment, ReportRegistration} from '../services/registrations-by-day/registrations-by-day.component';
import {RegistrationV2} from '../classes/registration';
import {Project} from '../classes/project';
import {ProjectService} from '../services/project.service';
import {Company} from '../classes/company';
import {CompanyService} from '../services/company.service';
import {debounceTime, forkJoin, merge, of, Subscription} from 'rxjs';
import {ChangeRegistrationProjectDialogComponent} from '../report/change-registration-project-dialog/change-registration-project-dialog.component';
import {MatDialog} from '@angular/material/dialog';
import {DatePipe, formatDate} from '@angular/common';
import {AzureBranchNamePipe} from '../pipes/azure-branch-name.pipe';
import {CompanyNamePipe} from '../pipes/company-name.pipe';
import {Utils} from '../utils.class';
import {AuthorisationService} from '../services/auth/authorisation.service';
import {MatButton, MatIconButton} from '@angular/material/button';
import {MatTooltip} from '@angular/material/tooltip';
import {EntryOrBreak, FieldComponent, FormatDurationPipe, PageComponent, PauseDividerComponent, PeriodStepperComponent, Person, PersonSwitcherComponent, Tone, voteFromAzure, withBreaks} from '../shared/ui';
import {TimeEntryComponent} from '../shared/ui/time-entry/time-entry.component';
import {PrCardComponent, PrReviewer, PrWorkItem} from '../shared/ui/pr-card/pr-card.component';
import {taskStatusTone} from '../shared/ui/task-row/task-row.component';

const PR_STATUS: Record<string, Tone> = {active: 'warn', completed: 'ok', abandoned: 'neutral'};

interface WeeklyPullRequestView {
    id: number;
    href: string;
    title: string;
    repo: string;
    branch: string;
    target: string;
    status: string;
    statusTone: Tone;
    when: string;
    reviewers: PrReviewer[];
    workItems: PrWorkItem[];
}

interface WeeklyDayView {
    date: string;
    name: string;
    dateLabel: string;
    today: boolean;
    weekend: boolean;
    future: boolean;
    users: (DayUser & { rows: EntryOrBreak<ReportRegistration>[] })[];
    totalMinutes: number;
    short: boolean;
    emptyText: string;
    prs: WeeklyPullRequestView[];
}


export interface FormGroupSelected {
    selectedUser: FormControl<null | number>;
    selectedCompany: FormControl<null | number>;
    selectedYear: FormControl<null | number>;
    selectedWeek: FormControl<null | number>;
}

@Component({
    selector: 'app-weekly',
    templateUrl: './weekly.component.html',
    styleUrls: ['./weekly.component.scss'],
    imports: [
        PortalModule,
        NgSelectComponent,
        ReactiveFormsModule,
        DatePipe,
        CompanyNamePipe,
        MatButton,
        MatIconButton,
        MatTooltip,
        PageComponent,
        PeriodStepperComponent,
        PersonSwitcherComponent,
        FieldComponent,
        PauseDividerComponent,
        FormatDurationPipe,
        TimeEntryComponent,
        PrCardComponent,
    ]
})
export class WeeklyComponent extends CodaltComponent implements OnInit {
    @ViewChild('taskContextMenu') taskContextMenuTpl: TemplateRef<unknown>;
    readonly contextMenuTask = signal<null | ReportRegistration>(null);

    readonly years = signal<{ value: null | number, name: string }[]>([]);
    readonly weeks = signal<{ value: null | number, name: string }[]>([]);
    readonly users = signal<User[]>([]);
    readonly projects = signal<Project[]>(null);
    readonly companies = signal<Company[]>([]);
    readonly registrationsByDay = signal<{ date: string, users: DayUser[] }[]>([]);
    readonly pullRequestsByDay = signal<{ date: string, prs: AzureWeeklyPullRequest[] }[]>([]);
    readonly weekDates = signal<string[]>([]);
    readonly showWeekPicker = signal(false);

    readonly people = computed<Person[]>(() => this.users().map(user => ({
        id: user.id,
        name: user.name,
        suffix: user.id === LocalStorage.getUser()?.id ? '(jij)' : undefined
    })));

    /** Presentation of the 7 day columns and the pull request row below them. */
    readonly days = computed<WeeklyDayView[]>(() => {
        const today = this.localDate(new Date());
        return this.weekDates().map(date => {
            const day = new Date(`${date}T00:00:00Z`);
            const dayOfWeek = day.getUTCDay();
            const weekend = dayOfWeek === 0 || dayOfWeek === 6;
            const future = date > today;
            const users = (this.registrationsByDay().find(d => d.date === date)?.users ?? []).map(user => ({
                ...user,
                rows: withBreaks(user.registrations, r => ({start: r.starttime, end: r.endtime}))
            }));
            const totalMinutes = users.reduce((sum, user) => sum + user.totalMinutes, 0);
            return {
                date,
                name: formatDate(day, 'EEEE', 'nl', 'UTC').replace(/^./, letter => letter.toUpperCase()),
                dateLabel: formatDate(day, 'd MMM y', 'nl', 'UTC'),
                today: date === today,
                weekend,
                future,
                users,
                totalMinutes,
                // README open point 4: a working day under 6 hours is orange
                short: !weekend && !future && totalMinutes > 0 && totalMinutes < 6 * 60,
                emptyText: weekend ? 'Weekend' : future ? 'Nog niet' : 'Geen registraties',
                prs: ((this.pullRequestsByDay().find(d => d.date === date)?.prs) ?? []).map(pr => this.pullRequestView(pr))
            };
        });
    });

    /** Weekend columns without registrations and pull requests are narrower. */
    readonly gridColumns = computed(() => this.days()
        .map(day => day.weekend && !day.users.length && !day.prs.length ? 'minmax(120px, .45fr)' : 'minmax(250px, 1fr)')
        .join(' '));

    readonly weekTotalMinutes = computed(() => this.days().reduce((sum, day) => sum + day.totalMinutes, 0));

    readonly weekRange = computed(() => {
        const dates = this.weekDates();
        if (!dates.length) {
            return '';
        }
        const label = (date: string) => formatDate(`${date}T00:00:00Z`, 'd MMM', 'nl', 'UTC');
        return `${label(dates[0])} – ${label(dates[dates.length - 1])}`;
    });

    readonly form = signal<FormGroup<ControlsOf<FormGroupSelected>>>(null);

    currentYear = new Date().getFullYear();
    isWeekExplicitlySelected = false;
    hasManagementRole = AuthorisationService.hasRole(this.Roles.Management);

    private reports: {
        period: { iso_period_year: number, period: number, start: string, end: string },
        registrations: RegistrationV2[],
        comments: ReportComment[]
    } = null;
    private reportsFetchSubscription: Subscription | null = null;
    private pullRequestsFetchSubscription: Subscription | null = null;
    private contextMenuOverlayRef: OverlayRef | null = null;

    constructor(
        private azureService: AzureService,
        private dialog: MatDialog,
        private overlay: Overlay,
        private registrationService: RegistrationService,
        private companyService: CompanyService,
        private userService: UserService,
        private viewContainerRef: ViewContainerRef,
        private router: ActivatedRoute,
        private projectService: ProjectService,
    ) {
        super();
    }

    @HostListener('document:click')
    closeContextMenu(): void {
        this.contextMenuOverlayRef?.dispose();
        this.contextMenuOverlayRef = null;
    }

    ngOnInit(): void {
        this.subscriptions.add(
            this.router.queryParams.subscribe(params => {
                const isPuppeteer = !!params['token'];
                const {year: currentYear, week: currentWeek} = this.currentIsoWeek();

                let weekValue: number;
                if (params['week']) {
                    this.isWeekExplicitlySelected = true;
                    weekValue = parseInt(params['week'])
                } else {
                    weekValue = isPuppeteer ? null : currentWeek
                }
                this.form.set(new FormGroup<ControlsOf<FormGroupSelected>>({
                    selectedUser: new FormControl(params['userId'] ? parseInt(params['userId']) : (isPuppeteer ? null : LocalStorage.getUser().id)),
                    selectedCompany: new FormControl(params['companyId'] ? parseInt(params['companyId']) : null),
                    selectedYear: new FormControl(params['year'] ? parseInt(params['year']) : (isPuppeteer ? null : currentYear)),
                    selectedWeek: new FormControl(weekValue),
                }));
            })
        );

        this.years.set([
            ...Array.from({length: new Date().getFullYear() - 2014}, (_, i) => ({value: 2015 + i, name: String(2015 + i)}))
        ]);

        this.setWeeksForYear(this.form()?.controls.selectedYear.value ?? new Date().getFullYear());

        this.subscriptions.add(
            this.userService.getList().subscribe({
                next: users => {
                    this.users.set(!this.hasManagementRole ? users.filter(u => u.id === this.form()?.controls.selectedUser.value) : users.filter(user => {
                        const now = new Date();
                        return user.employment_end === null || new Date(user.employment_end) < now;
                    }));
                },
                error: err => console.error(err)
            })
        );

        this.subscriptions.add(
            this.companyService.getCompanies(true).subscribe({
                next: companies => {
                    this.companies.set(companies);
                },
                error: err => console.error(err)
            })
        );

        this.subscriptions.add(
            merge(
                this.form()?.controls.selectedUser.valueChanges,
                this.form()?.controls.selectedCompany.valueChanges,
                this.form()?.controls.selectedYear.valueChanges,
                this.form()?.controls.selectedWeek.valueChanges,
            )
                .pipe(debounceTime(50))
                .subscribe(() => {
                    this.fetchWeek();
                })
        );

        this.subscriptions.add(
            this.projectService.getAll().subscribe({
                next: projects => {
                    this.projects.set(projects);
                    this.fetchWeek();
                },
                error: err => console.error(err)
            })
        );
    }

    fetchWeek() {
        const year: number | null = this.form()?.controls.selectedYear.value;
        const userId: number | null = this.form()?.controls.selectedUser.value;
        const companyId: number | null = this.form()?.controls.selectedCompany?.value;
        const week: number | null = this.form()?.controls.selectedWeek.value;

        this.reportsFetchSubscription?.unsubscribe();
        this.pullRequestsFetchSubscription?.unsubscribe();
        this.registrationsByDay.set([]);
        this.pullRequestsByDay.set([]);

        if (year && week) {
            const monday = new Date(Date.UTC(year, 0, 1));
            // Set to the Monday of the requested ISO week
            const dayOfWeek = Utils.dutchDayOfWeek(monday);
            monday.setUTCDate(monday.getUTCDate() + (week - 1) * 7 + (1 - dayOfWeek) + (dayOfWeek <= 4 ? 0 : 7));
            this.weekDates.set(Array.from({length: 7}, (_, i) => {
                const d = new Date(monday);
                d.setUTCDate(monday.getUTCDate() + i);
                return d.toISOString().substring(0, 10);
            }));
        } else {
            this.weekDates.set([]);
        }

        this.reportsFetchSubscription = (year ? this.registrationService.getDetailsByWeek(year, week, userId, companyId) : of({period: null, registrations: [], comments: []}))
            .subscribe({
                next: reports => {
                    this.reports = reports;
                    this.updateRegistrations(userId);
                },
                error: err => {
                    this.reports = {period: null, registrations: [], comments: []};
                    this.registrationsByDay.set([]);
                    console.error(err);
                }
            });

        this.pullRequestsFetchSubscription = this.azureService
            .getWeeklyPullRequests(year, week, this.users().find(user => user.id === userId)?.email)
            .subscribe({
                next: pullRequests => this.updatePullRequests(pullRequests),
                error: err => {
                    this.pullRequestsByDay.set([]);
                    console.error(err);
                }
            });

        this.subscriptions.add(this.reportsFetchSubscription);
        this.subscriptions.add(this.pullRequestsFetchSubscription);
    }

    updatePullRequests(projectPRs: { project: string; prs: AzureWeeklyPullRequest[] }[]) {
        const allPrs = projectPRs?.flatMap(p => p.prs ?? []) ?? [];

        const byDate = new Map<string, AzureWeeklyPullRequest[]>();
        for (const pr of allPrs) {
            const date = new Date(pr.creationDate).toISOString().substring(0, 10);
            if (!byDate.has(date)) {
                byDate.set(date, []);
            }
            byDate.get(date).push(pr);
        }

        // Sort PRs within each day ascending by creationDate
        for (const [, prs] of byDate) {
            prs.sort((a, b) => new Date(a.creationDate).getTime() - new Date(b.creationDate).getTime());
        }

        const sorted = [...byDate.entries()]
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([date, prs]) => ({date, prs}));

        this.pullRequestsByDay.set(sorted);
    }

    updateRegistrations(filteredUserId: number | null = this.form()?.controls.selectedUser.value) {
        const allRegistrations = this.reports.registrations.filter(r => !filteredUserId || +r.user_id === +filteredUserId);
        const allComments = this.reports.comments ?? [];
        const uniqueDates = [...new Set(allRegistrations.map(r => r.starttime.substring(0, 10)))].sort();
        const byDay = this.ByDay(uniqueDates, allRegistrations, allComments);

        this.registrationsByDay.set(byDay);
    }

    isoWeeksInYear(year: number): number {
        // Dec 28 is always in the last ISO week of the year
        const dec28 = new Date(year, 11, 28);
        const startOfYear = new Date(year, 0, 1);
        return Math.ceil(((dec28.getTime() - startOfYear.getTime()) / 86400000 + startOfYear.getDay() + 1) / 7);
    }

    setWeeksForYear(year: number): void {
        const count = this.isoWeeksInYear(year);
        this.weeks.set([
            ...Array.from({length: count}, (_, i) => ({value: i + 1, name: String(i + 1)}))
        ]);
    }

    currentIsoWeek(): { year: number, week: number } {
        const now = new Date();
        const startOfYear = new Date(now.getFullYear(), 0, 1);
        const week = Math.ceil(((now.getTime() - startOfYear.getTime()) / 86400000 + startOfYear.getDay() + 1) / 7);
        return {year: now.getFullYear(), week};
    }

    get isCurrentWeek(): boolean {
        const {year, week} = this.currentIsoWeek();
        return this.form()?.controls.selectedYear.value === year && this.form()?.controls.selectedWeek.value === week;
    }

    get isLastSelectableWeek(): boolean {
        const year = this.form()?.controls.selectedYear.value;
        return year === this.currentYear && this.form()?.controls.selectedWeek.value >= this.isoWeeksInYear(year);
    }

    /** Previous/next week of the stepper; crosses into the previous or next year. */
    stepWeek(step: 1 | -1) {
        const controls = this.form()?.controls;
        if (!controls?.selectedYear.value || !controls.selectedWeek.value) {
            return;
        }
        let year = controls.selectedYear.value;
        let week = controls.selectedWeek.value + step;
        if (week < 1) {
            year--;
            week = this.isoWeeksInYear(year);
        } else if (week > this.isoWeeksInYear(year)) {
            year++;
            week = 1;
        }
        if (year < 2015 || year > this.currentYear) {
            return;
        }
        this.isWeekExplicitlySelected = true;
        if (year !== controls.selectedYear.value) {
            controls.selectedYear.setValue(year);
            this.setWeeksForYear(year);
        }
        controls.selectedWeek.setValue(week);
    }

    goToCurrentWeek() {
        const {year, week} = this.currentIsoWeek();
        this.setWeeksForYear(year);
        this.form()?.controls.selectedYear.setValue(year);
        this.form()?.controls.selectedWeek.setValue(week);
    }

    registrationState(registration: ReportRegistration): 'approved' | 'disapproved' | null {
        if (registration.approved_at && registration.approved_by) {
            return 'approved';
        }
        return registration.disapproved_at && registration.approved_by ? 'disapproved' : null;
    }

    /** Approves all registrations of the day that are not approved yet (was the button of registrations-by-day). */
    approveDay(users: DayUser[]): void {
        const registrationIds = users
            .flatMap(u => u.registrations)
            .filter(r => !r.approved_at)
            .map(r => r.id);

        if (!registrationIds.length) {
            return;
        }

        this.subscriptions.add(
            forkJoin(registrationIds.map(id => this.registrationService.approveRegistration(id))).subscribe({
                next: () => this.fetchWeek(),
                error: err => console.error(err)
            })
        );
    }

    private pullRequestView(pr: AzureWeeklyPullRequest): WeeklyPullRequestView {
        const branchName = new AzureBranchNamePipe();
        const created = new Date(pr.creationDate);
        const status = pr.status ?? '';
        return {
            id: pr.pullRequestId,
            href: 'https://dev.azure.com/codalt/' + pr.project + '/_git/' + pr.repository + '/pullrequest/' + pr.pullRequestId,
            title: pr.title,
            repo: pr.repository,
            branch: branchName.transform(pr.sourceRefName),
            target: branchName.transform(pr.targetRefName),
            status: status.charAt(0).toUpperCase() + status.slice(1),
            statusTone: PR_STATUS[status] ?? 'neutral',
            when: formatDate(created, 'EEE d MMM, HH:mm', 'nl'),
            reviewers: (pr.reviewers ?? []).map(reviewer => ({
                name: reviewer.displayName,
                seed: this.users().find(user => user.name?.toLowerCase() === reviewer.displayName?.toLowerCase())?.id ?? reviewer.displayName,
                vote: voteFromAzure(reviewer.vote)
            })),
            workItems: (pr.workItems ?? []).map(item => ({
                id: item.id,
                type: item.workItemType,
                state: item.state,
                stateTone: taskStatusTone(item.state),
                title: item.title
            }))
        };
    }

    private localDate(date: Date): string {
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    }

    private ByDay(uniqueDates: string[], allRegistrations: RegistrationV2[], allComments: ReportComment[]) {
        return uniqueDates.map(date => {
            const dayRegistrations = allRegistrations.filter(r => r.starttime.startsWith(date));
            const users = [...new Set(dayRegistrations.map(r => r.user_id))].map(userId => {
                const registrations = dayRegistrations.filter(r => r.user_id === userId);
                const sorted = [...registrations].sort((a, b) => a.starttime.localeCompare(b.starttime));
                return {
                    userId,
                    name: this.users().find(u => u.id === userId)?.name ?? `User ${userId}`,
                    totalMinutes: this.minutesFromRegistrations(registrations),
                    registrations: sorted.map((r, i) => ({
                        ...r,
                        project: this.findProject(r.parent_work_project_id) ?? {id: r.parent_work_project_id, company_id: r.company_id} as Project,
                        lightStart: i > 0 && sorted[i - 1].endtime === r.starttime,
                        lightEnd: i < sorted.length - 1 && r.endtime === sorted[i + 1].starttime,
                        comments: allComments
                            .filter(c => c.user_id === userId && c.datetime >= r.starttime && c.datetime < r.endtime)
                            .sort((a, b) => a.datetime.localeCompare(b.datetime))
                    }))
                };
            });
            return {date, users: users.sort((a, b) => a.name.localeCompare(b.name))};
        });
    }

    private findProject(projectId: number): Project | null {
        for (const project of this.projects()) {
            if (project.id === projectId) return project;
            const child = project.children?.find(c => c.id === projectId);
            if (child) return child;
        }
        return null;
    }

    private minutesFromRegistrations(registrations: RegistrationV2[]): number {
        return registrations.reduce((sum, r) => sum + (new Date(r.endtime).getTime() - new Date(r.starttime).getTime()) / 60000, 0);
    }

    openContextMenu(registration: ReportRegistration, event: MouseEvent): void {
        event.preventDefault();
        event.stopPropagation();


        this.contextMenuOverlayRef?.dispose();
        this.contextMenuOverlayRef = null;


        this.contextMenuTask.set(registration);

        this.contextMenuOverlayRef = this.overlay.create({
            positionStrategy: this.overlay.position().global()
                .left(`${event.clientX}px`)
                .top(`${event.clientY}px`),
            scrollStrategy: this.overlay.scrollStrategies.close(),
        });

        this.contextMenuOverlayRef.attach(new TemplatePortal(this.taskContextMenuTpl, this.viewContainerRef));
    }

    changeRegistrationProject(registration: ReportRegistration) {
        const dialogRef = this.dialog.open(ChangeRegistrationProjectDialogComponent, {
            data: {
                registration,
                projects: this.projects() ?? []
            },
            maxWidth: '95vw',
            width: '720px',
            disableClose: true,
            panelClass: 'position-relative'
        });

        this.subscriptions.add(
            dialogRef.afterClosed().subscribe((workProjectId: null | number) => {
                if (!workProjectId || workProjectId === registration.work_project_id) {
                    return;
                }

                this.subscriptions.add(
                    this.registrationService.changeProject(registration.id, workProjectId).subscribe({
                        next: () => this.fetchWeek(),
                        error: err => console.error(err)
                    })
                );
            })
        );
    }

    approveHour(registrationId: number) {
        this.subscriptions.add(
            this.registrationService.approveRegistration(registrationId).subscribe({
                next: () => this.fetchWeek(),
                error: err => console.error(err)
            })
        );
    }

    disapproveHour(registrationId: number) {
        this.subscriptions.add(
            this.registrationService.disapproveRegistration(registrationId).subscribe({
                next: () => this.fetchWeek(),
                error: err => console.error(err)
            })
        );
    }
}
